import {
  parseExpiryMs,
  SIGNED_FILE_URL_EXPIRY_SKEW_MS,
} from "src/shared/utils/signedFileUrlCache";

import { GetSponsorPostEventReportQuery } from "./graphql/getSponsorPostEventReport.generated";

/**
 * The report is served by HackerAPI's `sponsorPostEventReport` query, which
 * resolves the caller's sponsor company server-side and returns a freshly
 * signed, expiring download URL. There is no client-side company filtering —
 * a caller who is not entitled to a report simply gets `null`.
 */
export type SponsorPostEventReport = NonNullable<
  GetSponsorPostEventReportQuery["sponsorPostEventReport"]
>;

export const REPORT_YEAR = 2026;
export const REPORT_TITLE = `${REPORT_YEAR} Post-Event Report`;
export const DEFAULT_REPORT_DOWNLOAD_FILENAME = `hack-the-north-${REPORT_YEAR}-post-event-report.pdf`;

/** `setTimeout` truncates anything past its 32-bit signed millisecond range. */
export const MAX_REPORT_REFRESH_DELAY_MS = 2 ** 31 - 1;

/**
 * A signed URL is treated as unusable slightly before it actually lapses, so a
 * download that starts just under the wire still completes.
 */
export const isReportUrlStale = (
  expiry: unknown,
  now: number = Date.now()
): boolean => {
  const expiryMs = parseExpiryMs(expiry);
  if (expiryMs === null) return true;
  return expiryMs - SIGNED_FILE_URL_EXPIRY_SKEW_MS <= now;
};

/**
 * Milliseconds to wait before re-fetching the report so a long-open tab never
 * renders from a lapsed URL. `null` means there is nothing to schedule — the
 * expiry is unusable now, so the caller should refetch rather than wait.
 */
export const getReportRefreshDelayMs = (
  expiry: unknown,
  now: number = Date.now()
): number | null => {
  const expiryMs = parseExpiryMs(expiry);
  if (expiryMs === null) return null;

  const delay = expiryMs - SIGNED_FILE_URL_EXPIRY_SKEW_MS - now;
  if (delay <= 0) return null;

  return Math.min(delay, MAX_REPORT_REFRESH_DELAY_MS);
};

/**
 * HackerAPI stores the uploaded filename; fall back to a stable name so the
 * download is never called something opaque like a bare file id.
 */
export const getReportDownloadFilename = (name?: string | null): string => {
  const base = (name ?? "").trim().split(/[\\/]/).pop() ?? "";
  return base.toLowerCase().endsWith(".pdf")
    ? base
    : DEFAULT_REPORT_DOWNLOAD_FILENAME;
};

export const shouldShowPostEventReportCard = (
  released: boolean,
  report?: SponsorPostEventReport | null
): boolean => released && Boolean(report?.uri);
